import {isEmpty} from 'lodash-es';

import {
  AmountInput,
  Button,
  CheckBox,
  Input,
  SpinningLoader,
} from '@/components';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {AfriexAdminAmount, AfriexReferralTier} from '@/types';

import {TierActionType} from './types';
import useSignupBonusUpdate from './useSignupBonusUpdate';

interface SignupBonusProps extends Pick<UserModalProps, 'isOpen' | 'onClose'> {
  data?: AfriexReferralTier;
}

const SignupBonusForm = ({isOpen, onClose, data}: SignupBonusProps) => {
  const {dispatch, isLoading, handleSubmit, tier} = useSignupBonusUpdate(
    onClose,
    data,
  );

  const {
    name,
    isDeactivated,
    referralCode,
    bonusAmountPerReferree,
    currencyCode,
    id: referralId,
  } = tier;

  const updateAction = {
    type: TierActionType.UPDATE_TIER,
    payload: {},
  };
  const updateLabel = isEmpty(data)
    ? 'Create New Signup Bonus Tier +'
    : 'Update Signup Bonus Tier';
  const submitLabel = isEmpty(data) ? 'Submit' : 'Update';
  const isDisabled = isEmpty(tier);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-96"
      closeOnOutsideClick
      title={updateLabel}
    >
      {isLoading ? (
        <div className="flex w-full h-full justify-center items-center my-5">
          <SpinningLoader />
        </div>
      ) : (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // Fire-and-forget: useSignupBonusUpdate's handleSubmit
              // try/catches, clears `isLoading` and reports through
              // showErrorMessage / showSuccessMessage, so this only stops an
              // unexpected throw becoming an unhandled rejection.
              handleSubmit(tier).catch(() => {});
            }}
            className="flex flex-col space-y-5"
          >
            <div className="flex flex-col gap-3 px-2">
              <Input
                name="name"
                label="Name"
                type="text"
                value={name || ''}
                className="w-full"
                onChange={(e) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      name: e.target?.value || name,
                    },
                  })
                }
              />

              <AmountInput
                label="Bonus Amount Per Referee"
                fixedCurrency={referralId ? currencyCode : undefined}
                // `currencyCode` is optional on the tier while
                // `AfriexAdminAmount.currency` is required. AmountInput only
                // reads `value?.currency` behind a truthiness check, so a
                // missing code is already handled at runtime; the assertion
                // narrows what used to be a blanket `any`.
                value={
                  {
                    amount: bonusAmountPerReferree,
                    currency: currencyCode,
                  } as AfriexAdminAmount
                }
                onChange={(value: AfriexAdminAmount) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      bonusAmountPerReferree: value?.amount
                        ? Number(value?.amount)
                        : bonusAmountPerReferree,
                      currencyCode: value?.currency
                        ? value?.currency
                        : currencyCode,
                    },
                  })
                }
              />

              <Input
                name="referralCode"
                label="Referral Code"
                type="text"
                value={referralCode || ''}
                className="w-full"
                onChange={(e) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      referralCode: e.target?.value?.toLowerCase() || '',
                    },
                  })
                }
              />

              <CheckBox
                label="Mark as active"
                type="checkbox"
                checked={!isDeactivated}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      isDeactivated: !e.target?.checked,
                    },
                  })
                }
              />

              <div className="flex w-full justify-end space-x-2 py-3">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  colorScheme="cyan"
                >
                  Cancel
                </Button>

                <Button
                  disabled={isDisabled}
                  type="submit"
                  variant="solid"
                  colorScheme="cyan"
                >
                  {submitLabel}
                </Button>
              </div>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default SignupBonusForm;
